import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import usePageMeta from '../../services/page-meta';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';

import '../../assets/styles/events/conferenceRegistration.scss';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /(.+)?([1-9][0-9][0-9]{7})/;

const EMPTY_FORM = {
    lastName: '',
    firstName: '',
    middleName: '',
    region: '',
    district: '',
    city: '',
    workplaceName: '',
    position: '',
    medicalSpeciality: '',
    phone: '',
    email: '',
};

function normalizeId(value) {
    if (value === null || typeof value === 'undefined' || value === '') {
        return '';
    }

    return String(value);
}

function normalizePhone(value) {
    return String(value || '').replace(/[()\s]+/g, '');
}

export default function EventRegistrationPage() {
    const {id} = useParams();
    const {t} = useTranslation();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [conference, setConference] = useState({name: '', image: null});
    const [isRegistered, setIsRegistered] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [regionOptions, setRegionOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [specialityOptions, setSpecialityOptions] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const pageTitle = conference?.name || t('meta.conferenceRegistration.title', 'Conference registration');

    usePageMeta({
        title: pageTitle,
        description: t('meta.conferenceRegistration.description', t('app.description')),
        keywords: t('meta.conferenceRegistration.keywords', t('app.keywords')),
    });

    const loadDistricts = useCallback(async (regionId) => {
        if (!regionId) {
            setDistrictOptions([]);
            return;
        }

        try {
            const response = await fetch(`/api/v1/location/region/${regionId}/district`, {
                credentials: 'same-origin',
            });
            if (!response.ok) {
                throw new Error('Cannot load districts');
            }

            const result = await response.json();
            setDistrictOptions(Array.isArray(result) ? result : []);
        } catch (e) {
            setDistrictOptions([]);
        }
    }, []);

    const loadCities = useCallback(async (districtId) => {
        if (!districtId) {
            setCityOptions([]);
            return;
        }

        try {
            const response = await fetch(`/api/v1/location/district/${districtId}/cities`, {
                credentials: 'same-origin',
            });
            if (!response.ok) {
                throw new Error('Cannot load cities');
            }

            const result = await response.json();
            setCityOptions(Array.isArray(result) ? result : []);
        } catch (e) {
            setCityOptions([]);
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        setLoading(true);
        setNotFound(false);
        fetch(`/api/v1/conference/${id}/registration`, {credentials: 'same-origin'})
            .then(async (response) => {
                const result = await response.json().catch(() => null);
                const {kind, redirectUrl} = parseRouteResponse(response, result);

                if (kind === 'not_found') {
                    if (isMounted) {
                        setConference({name: '', image: null});
                        setNotFound(true);
                    }
                    return null;
                }

                if ((kind === 'unauthorized' || kind === 'redirect' || kind === 'error') && redirectUrl) {
                    window.location.href = redirectUrl;
                    return null;
                }

                if (kind === 'error') {
                    throw new Error('Cannot load registration page data');
                }

                return result;
            })
            .then((result) => {
                if (!isMounted) {
                    return;
                }

                if (!result) {
                    return;
                }

                const serverForm = result?.form || {};
                const nextForm = {
                    lastName: serverForm.lastName || '',
                    firstName: serverForm.firstName || '',
                    middleName: serverForm.middleName || '',
                    region: normalizeId(serverForm.regionId),
                    district: normalizeId(serverForm.districtId),
                    city: normalizeId(serverForm.cityId),
                    workplaceName: serverForm.workplaceName || '',
                    position: normalizeId(serverForm.positionId),
                    medicalSpeciality: normalizeId(serverForm.medicalSpecialityId),
                    phone: serverForm.phone || '',
                    email: serverForm.email || '',
                };

                setConference({
                    name: result?.conference?.name || '',
                    image: result?.conference?.image || null,
                });
                setIsRegistered(Boolean(result?.isRegistered));
                setForm(nextForm);
                setRegionOptions(Array.isArray(result?.options?.regions) ? result.options.regions : []);
                setPositionOptions(Array.isArray(result?.options?.positions) ? result.options.positions : []);
                setSpecialityOptions(Array.isArray(result?.options?.specialities) ? result.options.specialities : []);
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                setConference({name: '', image: null});
                setNotFound(false);
            })
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [id]);

    useEffect(() => {
        loadDistricts(form.region);
    }, [form.region, loadDistricts]);

    useEffect(() => {
        loadCities(form.district);
    }, [form.district, loadCities]);

    const validate = useCallback((values) => {
        const requiredMessage = t('login.errors.required');
        const incorrectDataMessage = t('login.errors.incorrectData');
        const emailMessage = t('login.errors.email');

        const nextErrors = {};
        const requiredFields = ['lastName', 'firstName', 'middleName', 'region', 'workplaceName', 'position', 'medicalSpeciality', 'phone', 'email'];
        requiredFields.forEach((field) => {
            if (!String(values[field] || '').trim()) {
                nextErrors[field] = requiredMessage;
            }
        });

        if (values.email && !EMAIL_REGEX.test(values.email)) {
            nextErrors.email = emailMessage;
        }

        if (values.phone && !PHONE_REGEX.test(normalizePhone(values.phone))) {
            nextErrors.phone = incorrectDataMessage;
        }

        return nextErrors;
    }, [t]);

    const validateField = useCallback((name, value) => {
        const nextErrors = validate({...form, [name]: value});
        setErrors((prev) => ({
            ...prev,
            [name]: nextErrors[name],
        }));
    }, [form, validate]);

    const onTextFieldChange = (name) => (event) => {
        const value = name === 'phone' ? normalizePhone(event.target.value) : event.target.value;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
        setErrors((prev) => ({...prev, [name]: null}));
        setSubmitError(false);
    };

    const onRegionChange = (event) => {
        const value = event.target.value;
        setForm((prev) => ({
            ...prev,
            region: value,
            district: '',
            city: '',
        }));
        setDistrictOptions([]);
        setCityOptions([]);
        setErrors((prev) => ({...prev, region: null, district: null, city: null}));
    };

    const onDistrictChange = (event) => {
        const value = event.target.value;
        setForm((prev) => ({
            ...prev,
            district: value,
            city: '',
        }));
        setCityOptions([]);
        setErrors((prev) => ({...prev, district: null, city: null}));
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        setSubmitError(false);

        const nextErrors = validate(form);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            const firstInvalid = Object.keys(nextErrors)[0];
            const firstInvalidElement = document.getElementById(`conference-registration-${firstInvalid}`);
            if (firstInvalidElement) {
                firstInvalidElement.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
            return;
        }

        const payload = new FormData();
        payload.set('lastName', form.lastName);
        payload.set('firstName', form.firstName);
        payload.set('middleName', form.middleName);
        payload.set('region', form.region);
        payload.set('district', form.district);
        payload.set('city', form.city);
        payload.set('workplaceName', form.workplaceName);
        payload.set('position', form.position);
        payload.set('medicalSpeciality', form.medicalSpeciality);
        payload.set('phone', normalizePhone(form.phone));
        payload.set('email', form.email);

        setSubmitting(true);
        try {
            const response = await fetch(`/api/v1/conference/${id}/register`, {
                method: 'POST',
                credentials: 'same-origin',
                body: payload,
            });

            const responsePayload = await response.json().catch(() => null);

            if (!response.ok) {
                const redirectUrl = responsePayload?.redirectUrl;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                throw new Error('Cannot submit conference registration form');
            }

            setIsRegistered(true);
        } catch (e) {
            setSubmitError(true);
        } finally {
            setSubmitting(false);
        }
    };

    const conferenceImageStyle = useMemo(() => {
        if (!conference.image) {
            return undefined;
        }

        return {
            backgroundImage: `url('${conference.image}')`,
        };
    }, [conference.image]);

    const labelClassName = (isSelect, hasValue) => {
        const classes = ['form-label'];
        if (isSelect) {
            classes.push('selectLabel');
        }
        if (hasValue) {
            classes.push('active');
        }

        return classes.join(' ');
    };

    const renderError = (field) => (
        <div className="form-error-block">
            <span className={errors[field] ? 'js-error' : 'hidden js-error'}>{errors[field] || ''}</span>
        </div>
    );

    if (loading) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    return (
        <section className="conference-registration conference-registration-page">
            <div className="conference-img" style={conferenceImageStyle} />
            <div className="container">
                <div className="row">
                    <nav className="breadcrumbs">
                        <ul>
                            <li>
                                <Link to="/conferences">{t('conferences.header')}</Link>
                            </li>
                            <li className="active">
                                {t('conferences.conference.registration.breadcrumbs')}
                            </li>
                        </ul>
                    </nav>
                </div>

                {isRegistered ? (
                    <div className="success-container">
                        <div className="success-block">
                            <p className="conference-name">{conference.name}</p>
                            <h1>{t('conferences.conference.registration.success')}</h1>
                            <div className="button-block">
                                <Link to={`/conference/${id}`} className="btn">
                                    {t('conferences.conference.registration.toConference')}
                                </Link>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="form-section">
                        <p className="conference-name">{conference.name}</p>
                        <h1>{t('conferences.conference.registration.header')}</h1>
                        <form className="form" id="registrationForm" onSubmit={onSubmit} noValidate>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-lastName" className={labelClassName(false, Boolean(form.lastName))}>
                                    {t('conferences.conference.registration.lastName')}
                                </label>
                                <input
                                    type="text"
                                    value={form.lastName}
                                    name="lastName"
                                    id="conference-registration-lastName"
                                    className={`form-input${errors.lastName ? ' error-field' : ''}`}
                                    onChange={onTextFieldChange('lastName')}
                                    onBlur={(event) => validateField('lastName', event.target.value)}
                                />
                                {renderError('lastName')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-firstName" className={labelClassName(false, Boolean(form.firstName))}>
                                    {t('conferences.conference.registration.firstName')}
                                </label>
                                <input
                                    type="text"
                                    value={form.firstName}
                                    name="firstName"
                                    id="conference-registration-firstName"
                                    className={`form-input${errors.firstName ? ' error-field' : ''}`}
                                    onChange={onTextFieldChange('firstName')}
                                    onBlur={(event) => validateField('firstName', event.target.value)}
                                />
                                {renderError('firstName')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-middleName" className={labelClassName(false, Boolean(form.middleName))}>
                                    {t('conferences.conference.registration.middleName')}
                                </label>
                                <input
                                    type="text"
                                    value={form.middleName}
                                    name="middleName"
                                    id="conference-registration-middleName"
                                    className={`form-input${errors.middleName ? ' error-field' : ''}`}
                                    onChange={onTextFieldChange('middleName')}
                                    onBlur={(event) => validateField('middleName', event.target.value)}
                                />
                                {renderError('middleName')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-region" className={labelClassName(true, Boolean(form.region))}>
                                    {t('conferences.conference.registration.region')}
                                </label>
                                <select
                                    className={`form-select${errors.region ? ' error-field' : ''}`}
                                    name="region"
                                    id="conference-registration-region"
                                    value={form.region}
                                    onChange={onRegionChange}
                                    onBlur={(event) => validateField('region', event.target.value)}
                                >
                                    <option value="" />
                                    {regionOptions.map((region) => (
                                        <option key={region.id} value={region.id}>{region.name}</option>
                                    ))}
                                </select>
                                {renderError('region')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-district" className={labelClassName(true, Boolean(form.district))}>
                                    {t('conferences.conference.registration.district')}
                                </label>
                                <select
                                    className="form-select"
                                    name="district"
                                    id="conference-registration-district"
                                    value={form.district}
                                    onChange={onDistrictChange}
                                    disabled={!form.region}
                                >
                                    <option value="" />
                                    {districtOptions.map((district) => (
                                        <option key={district.id} value={district.id}>{district.name}</option>
                                    ))}
                                </select>
                                {renderError('district')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-city" className={labelClassName(true, Boolean(form.city))}>
                                    {t('conferences.conference.registration.city')}
                                </label>
                                <select
                                    className="form-select"
                                    name="city"
                                    id="conference-registration-city"
                                    value={form.city}
                                    onChange={onTextFieldChange('city')}
                                    disabled={!form.district}
                                >
                                    <option value="" />
                                    {cityOptions.map((city) => (
                                        <option key={city.id} value={city.id}>{city.name}</option>
                                    ))}
                                </select>
                                {renderError('city')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-workplaceName" className={labelClassName(false, Boolean(form.workplaceName))}>
                                    {t('conferences.conference.registration.workPlace')}
                                </label>
                                <input
                                    type="text"
                                    value={form.workplaceName}
                                    name="workplaceName"
                                    id="conference-registration-workplaceName"
                                    className={`form-input${errors.workplaceName ? ' error-field' : ''}`}
                                    onChange={onTextFieldChange('workplaceName')}
                                    onBlur={(event) => validateField('workplaceName', event.target.value)}
                                />
                                {renderError('workplaceName')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-position" className={labelClassName(true, Boolean(form.position))}>
                                    {t('conferences.conference.registration.position')}
                                </label>
                                <select
                                    className={`form-select${errors.position ? ' error-field' : ''}`}
                                    name="position"
                                    id="conference-registration-position"
                                    value={form.position}
                                    onChange={onTextFieldChange('position')}
                                    onBlur={(event) => validateField('position', event.target.value)}
                                >
                                    <option value="" />
                                    {positionOptions.map((position) => (
                                        <option key={position.id} value={position.id}>{position.name}</option>
                                    ))}
                                </select>
                                {renderError('position')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-medicalSpeciality" className={labelClassName(true, Boolean(form.medicalSpeciality))}>
                                    {t('conferences.conference.registration.specialty')}
                                </label>
                                <select
                                    className={`form-select${errors.medicalSpeciality ? ' error-field' : ''}`}
                                    name="medicalSpeciality"
                                    id="conference-registration-medicalSpeciality"
                                    value={form.medicalSpeciality}
                                    onChange={onTextFieldChange('medicalSpeciality')}
                                    onBlur={(event) => validateField('medicalSpeciality', event.target.value)}
                                >
                                    <option value="" />
                                    {specialityOptions.map((speciality) => (
                                        <option key={speciality.id} value={speciality.id}>{speciality.name}</option>
                                    ))}
                                </select>
                                {renderError('medicalSpeciality')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-phone" className={labelClassName(false, Boolean(form.phone))}>
                                    {t('conferences.conference.registration.phone')}
                                </label>
                                <input
                                    type="text"
                                    value={form.phone}
                                    name="phone"
                                    id="conference-registration-phone"
                                    className={`form-input${errors.phone ? ' error-field' : ''}`}
                                    onChange={onTextFieldChange('phone')}
                                    onBlur={(event) => validateField('phone', event.target.value)}
                                />
                                {renderError('phone')}
                            </div>
                            <div className="form-group js-form-group">
                                <label htmlFor="conference-registration-email" className={labelClassName(false, Boolean(form.email))}>
                                    {t('conferences.conference.registration.email')}
                                </label>
                                <input
                                    type="text"
                                    value={form.email}
                                    name="email"
                                    id="conference-registration-email"
                                    className={`form-input${errors.email ? ' error-field' : ''}`}
                                    onChange={onTextFieldChange('email')}
                                    onBlur={(event) => validateField('email', event.target.value)}
                                />
                                {renderError('email')}
                            </div>
                            {submitError && (
                                <div className="form-group js-form-group">
                                    <div className="form-error-block">
                                        <span className="js-error">{t('login.errors.incorrectData')}</span>
                                    </div>
                                </div>
                            )}
                            <div className="submit-block">
                                <input
                                    type="submit"
                                    value={t('conferences.conference.registration.submit')}
                                    className="btn js-submit-btn"
                                    disabled={submitting}
                                />
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </section>
    );
}
